import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Cake, MessageSquare, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const txt = (lang, fr, ru, md, uk) => ({ fr, ru, md, uk }[lang] || fr);

const todayKey = () => {
  const d = new Date();
  return `lpc-bday-team-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Carte d'equipe du tableau de bord (Pavel 2026-08-02) : quand un collegue
 * fete son anniversaire aujourd'hui, tout le monde voit une carte chaleureuse
 * avec un bouton vers le chat pour lui souhaiter. Fermable pour la journee.
 */
export default function BirthdayBanner() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [others, setOthers] = useState([]);
  const [dismissed, setDismissed] = useState(!!localStorage.getItem(todayKey()));

  useEffect(() => {
    if (!user?.id || dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/birthdays/today`);
        if (!cancelled) {
          setOthers((res.data || []).filter((u) => u.id !== user.id));
        }
      } catch (e) { /* silencieux */ }
    })();
    return () => { cancelled = true; };
  }, [user?.id, dismissed]);

  if (dismissed || others.length === 0) return null;

  const names = others.map((u) => (u.name || '').split(' ')[0]).join(', ');
  const single = others.length === 1;

  const dismiss = () => {
    localStorage.setItem(todayKey(), '1');
    setDismissed(true);
  };

  return (
    <div
      className="card-base p-4 bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-200 flex items-center gap-4"
      data-testid="birthday-banner"
    >
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center flex-shrink-0 shadow">
        <Cake className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-bold text-gray-800">
          {txt(language,
            `🎂 Aujourd'hui, c'est l'anniversaire de ${names} !`,
            `🎂 Сегодня день рождения у ${names}!`,
            `🎂 Astăzi este ziua de naștere a lui ${names}!`,
            `🎂 Сьогодні день народження у ${names}!`)}
        </p>
        <p className="text-sm text-gray-600">
          {single
            ? txt(language, 'Pense à lui souhaiter !', 'Не забудь поздравить!', 'Nu uita să-l feliciți!', 'Не забудь привітати!')
            : txt(language, 'Pense à leur souhaiter !', 'Не забудь их поздравить!', 'Nu uita să-i feliciți!', 'Не забудь їх привітати!')}
        </p>
      </div>
      <button
        onClick={() => navigate('/chat')}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white text-sm font-semibold shadow flex-shrink-0"
        data-testid="birthday-banner-chat"
      >
        <MessageSquare className="w-4 h-4" />
        {txt(language, 'Souhaiter 🎉', 'Поздравить 🎉', 'Felicită 🎉', 'Привітати 🎉')}
      </button>
      <button
        onClick={dismiss}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/70 rounded-full flex-shrink-0"
        aria-label={txt(language, 'Fermer', 'Закрыть', 'Închide', 'Закрити')}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
